/**
 * FunShine Application Shell
 * Router, State Manager, Cartridge Lifecycle, Realtime Multiplayer & Modals
 */
import { auth } from './services/auth.js';
import { sound } from './services/audio.js';
import { multiplayer } from './services/multiplayer.js';
import { getAllCartridges, getCartridge } from './cartridges/registry.js';
import {
  isSupabaseConfigured,
  setCustomCredentials,
  clearCustomCredentials,
  SUPABASE_URL
} from './services/supabase.js';

class AppShell {
  constructor() {
    this.currentCartridge = null;
    this.currentSession = null;
    this.activeRoute = '/';
    this.toasts = [];

    this.mainView = document.getElementById('main-view');
    this.modalContainer = document.getElementById('modal-container');
    this.toastContainer = document.getElementById('toast-container');
    this.offlineBanner = document.getElementById('offline-banner');

    this.playerAvatar = document.getElementById('header-player-avatar');
    this.playerName = document.getElementById('header-player-name');
  }

  async init() {
    // 1. Initialize Authentication & Profile
    await auth.init();
    auth.subscribe((profile) => this.updatePlayerHeader(profile));

    // 2. Setup Route & Hash Listener
    window.addEventListener('hashchange', () => this.handleHashChange());

    // 3. Setup Navigation & Header Actions
    this.setupNavigation();

    // 4. Offline / Online Event Listeners
    window.addEventListener('online', () => this.updateNetworkStatus());
    window.addEventListener('offline', () => this.updateNetworkStatus());
    this.updateNetworkStatus();

    // 5. Register PWA Service Worker
    this.registerServiceWorker();

    // 6. Initial Route Load
    await this.handleHashChange();
  }

  updatePlayerHeader(profile) {
    if (!profile) return;
    if (this.playerAvatar) this.playerAvatar.textContent = profile.avatar_emoji || '🎮';
    if (this.playerName) this.playerName.textContent = profile.display_name || 'Player';
  }

  updateNetworkStatus() {
    if (!this.offlineBanner) return;
    if (navigator.onLine) {
      this.offlineBanner.classList.remove('visible');
    } else {
      this.offlineBanner.classList.add('visible');
    }
  }

  registerServiceWorker() {
    if ('serviceWorker' in navigator && window.location.protocol.startsWith('http')) {
      navigator.serviceWorker
        .register('./sw.js')
        .then(() => console.log('[PWA] Service Worker registered'))
        .catch((err) => console.warn('[PWA] Service Worker registration failed:', err));
    }
  }

  setupNavigation() {
    document.getElementById('header-brand-btn')?.addEventListener('click', () => {
      this.navigate('/');
    });

    document.getElementById('header-player-pill')?.addEventListener('click', () => {
      this.navigate('/profile');
    });

    document.getElementById('header-settings-btn')?.addEventListener('click', () => {
      this.navigate('/settings');
    });

    document.querySelectorAll('.nav-item').forEach((item) => {
      item.addEventListener('click', () => {
        const route = item.getAttribute('data-route');
        this.navigate(route);
      });
    });
  }

  navigate(path) {
    window.location.hash = path;
  }

  async handleHashChange() {
    const rawHash = window.location.hash.slice(1) || '/';
    const [pathPart, queryPart] = rawHash.split('?');
    const queryParams = new URLSearchParams(queryPart || '');

    // Cleanup active cartridge if leaving game view
    if (!pathPart.startsWith('/game') && this.currentCartridge) {
      this.destroyCurrentCartridge();
    }

    // Update Bottom Nav active state
    document.querySelectorAll('.nav-item').forEach((item) => {
      const route = item.getAttribute('data-route');
      if (route === '/' && pathPart === '/') {
        item.classList.add('active');
      } else if (route !== '/' && pathPart.startsWith(route)) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });

    // Handle Routes
    if (pathPart === '/' || pathPart === '') {
      this.renderArcadeLibrary();
    } else if (pathPart.startsWith('/game/')) {
      const gameId = pathPart.replace('/game/', '');
      await this.startSoloGame(gameId, queryParams);
    } else if (pathPart.startsWith('/join/')) {
      const code = pathPart.replace('/join/', '');
      await this.joinMultiplayerRoom(code);
    } else if (pathPart === '/profile') {
      this.renderProfileView();
    } else if (pathPart === '/leaderboards') {
      this.renderLeaderboardsView();
    } else if (pathPart === '/settings') {
      this.renderSettingsView();
    }
  }

  showToast(message, type = 'info') {
    const toast = document.createElement('div');
    toast.className = `toast ${type === 'success' ? 'toast-success' : type === 'error' ? 'toast-error' : ''}`;
    toast.textContent = message;
    this.toastContainer.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 250);
    }, 3200);
  }

  destroyCurrentCartridge() {
    if (this.currentCartridge) {
      try {
        this.currentCartridge.destroy();
      } catch (e) {
        console.warn('Error destroying cartridge:', e);
      }
      this.currentCartridge = null;
    }
    if (this.currentSession) {
      this.currentSession.leave();
      this.currentSession = null;
    }
  }

  /* -------------------------------------------------------------------------
     VIEW: Arcade Library
     ------------------------------------------------------------------------- */
  renderArcadeLibrary() {
    const games = getAllCartridges();

    this.mainView.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
        <h1 style="font-size:1.4rem; font-weight:800;">Arcade Library</h1>
        <button id="lib-join-code-btn" class="btn btn-secondary btn-sm">🔑 Enter Code</button>
      </div>
      <p style="font-size:0.85rem; color:var(--text-muted); margin-bottom:12px;">
        Play against the computer or challenge a friend via invite link.
      </p>

      <div class="arcade-grid">
        ${games.map((g) => `
          <div class="game-card" data-id="${g.id}">
            <div class="game-card-header">
              <div class="game-icon-badge">${g.icon}</div>
              <div class="game-info">
                <div class="game-title">
                  <span>${g.name}</span>
                  <span style="font-size:0.7rem; background:var(--bg-surface); padding:2px 8px; border-radius:999px; border:1px solid var(--border-subtle); color:var(--accent-cyan); font-weight:700;">
                    ${g.category}
                  </span>
                </div>
                <div class="game-tagline">${g.tagline}</div>
              </div>
            </div>
            <div class="game-card-actions">
              <button class="btn btn-secondary btn-solo" data-id="${g.id}">
                🤖 Solo Play
              </button>
              <button class="btn btn-primary btn-challenge" data-id="${g.id}">
                ⚔️ 1v1 Challenge
              </button>
            </div>
          </div>
        `).join('')}
      </div>
    `;

    document.getElementById('lib-join-code-btn')?.addEventListener('click', () => {
      this.openJoinCodeModal();
    });

    this.mainView.querySelectorAll('.btn-solo').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        this.openSoloConfigModal(id);
      });
    });

    this.mainView.querySelectorAll('.btn-challenge').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        this.createMultiplayerRoom(id);
      });
    });
  }

  /* -------------------------------------------------------------------------
     MODAL: Solo Configuration
     ------------------------------------------------------------------------- */
  openSoloConfigModal(gameId) {
    const game = getCartridge(gameId);
    if (!game) return;

    sound.playSfx('tap');

    const optionsHtml = (game.configOptions || [])
      .map((opt) => `
        <div class="form-group">
          <label class="form-label">${opt.label}</label>
          <select class="form-select config-input" data-key="${opt.key}">
            ${opt.options.map((o) => `<option value="${o.value}">${o.label}</option>`).join('')}
          </select>
        </div>
      `)
      .join('');

    this.openModal(`
      <div class="modal-header">
        <h3 class="modal-title">${game.icon} ${game.name} — Solo Play</h3>
        <button class="btn-icon modal-close">✕</button>
      </div>
      <div>
        ${optionsHtml}
        <button id="modal-start-solo-btn" class="btn btn-primary btn-block" style="margin-top:12px;">
          🚀 Start Game
        </button>
      </div>
    `);

    document.getElementById('modal-start-solo-btn')?.addEventListener('click', () => {
      const config = {};
      this.modalContainer.querySelectorAll('.config-input').forEach((select) => {
        config[select.getAttribute('data-key')] = select.value;
      });
      this.closeModal();
      const params = new URLSearchParams(config);
      this.navigate(`/game/${gameId}?mode=solo&${params.toString()}`);
    });
  }

  /* -------------------------------------------------------------------------
     SOLO GAME RUNNER
     ------------------------------------------------------------------------- */
  async startSoloGame(gameId, queryParams) {
    const cartridge = getCartridge(gameId);
    if (!cartridge) {
      this.showToast('Game cartridge not found!', 'error');
      this.navigate('/');
      return;
    }

    this.destroyCurrentCartridge();
    this.currentCartridge = cartridge;

    const config = Object.fromEntries(queryParams.entries());
    const profile = auth.getProfile();

    this.mainView.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; width:100%; margin-bottom:8px;">
        <button id="game-exit-btn" class="btn btn-secondary btn-sm">← Exit Game</button>
        <span style="font-weight:700; font-size:0.9rem;">${cartridge.icon} ${cartridge.name}</span>
        <button id="game-restart-btn" class="btn btn-secondary btn-sm">🔄 Restart</button>
      </div>
      <div id="cartridge-mount" class="cartridge-stage"></div>
    `;

    document.getElementById('game-exit-btn')?.addEventListener('click', () => {
      this.navigate('/');
    });

    document.getElementById('game-restart-btn')?.addEventListener('click', () => {
      this.startSoloGame(gameId, queryParams);
    });

    const mount = document.getElementById('cartridge-mount');

    await cartridge.init(mount, {
      mode: 'solo',
      playerId: profile.id,
      opponentId: 'ai',
      opponentName: 'Computer',
      isHost: true,
      config,
      audio: sound,
      haptics: {
        vibrate: (pattern) => sound.vibrate(pattern)
      },
      onGameOver: (result) => {
        auth.recordLocalSoloResult(result.winnerId === profile.id, result.isDraw);
        this.openGameOverModal(result, () => this.startSoloGame(gameId, queryParams));
      }
    });
  }

  /* -------------------------------------------------------------------------
     MULTIPLAYER: Room Creation & Lobby
     ------------------------------------------------------------------------- */
  async createMultiplayerRoom(gameId) {
    const game = getCartridge(gameId);
    if (!game) return;

    sound.playSfx('tap');
    this.showToast('Creating multiplayer room...');

    try {
      const session = await multiplayer.createRoom(gameId, game.defaultConfig);
      this.currentSession = session;
      this.openLobbyModal(session, game);
    } catch (err) {
      this.showToast(err.message, 'error');
    }
  }

  async joinMultiplayerRoom(roomCode) {
    sound.playSfx('tap');
    this.showToast(`Joining room ${roomCode}...`);

    try {
      const session = await multiplayer.joinRoom(roomCode);
      this.currentSession = session;
      const game = getCartridge(session.room.game_id);
      this.openLobbyModal(session, game);
    } catch (err) {
      this.showToast(err.message, 'error');
      this.navigate('/');
    }
  }

  openLobbyModal(session, game) {
    const code = session.room.code;
    const shareUrl = `${window.location.origin}${window.location.pathname}#/join/${code}`;

    this.openModal(`
      <div class="modal-header">
        <h3 class="modal-title">${game.icon} ${game.name} — Lobby</h3>
        <button class="btn-icon modal-close">✕</button>
      </div>

      <div style="text-align:center;">
        <p style="font-size:0.85rem; color:var(--text-muted);">Share this code or link with your opponent:</p>
        <div class="lobby-code-box">
          <div class="lobby-code">${code}</div>
        </div>

        <div style="display:flex; gap:8px; margin-bottom:16px;">
          <button id="lobby-copy-btn" class="btn btn-secondary" style="flex:1;">📋 Copy Link</button>
          <button id="lobby-share-btn" class="btn btn-accent" style="flex:1;">📲 Native Share</button>
        </div>

        <h4 style="font-size:0.85rem; color:var(--text-muted); text-align:left; text-transform:uppercase;">Connected Players</h4>
        <div id="lobby-peers" class="lobby-presence-list">
          <div class="peer-badge">
            <span>👤 You (${auth.getProfile().display_name})</span>
            <div class="peer-status-dot"></div>
          </div>
          <div id="lobby-guest-badge" class="peer-badge" style="opacity:0.6;">
            <span>⏳ Waiting for opponent to join...</span>
          </div>
        </div>

        <button id="lobby-start-btn" class="btn btn-primary btn-block" style="margin-top:14px;" ${session.isHost ? '' : 'disabled'}>
          ${session.isHost ? '⚔️ Start Match' : '⏳ Waiting for Host to Start'}
        </button>
      </div>
    `);

    // Copy Link button
    document.getElementById('lobby-copy-btn')?.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(shareUrl);
        sound.playSfx('success');
        this.showToast('Invite link copied to clipboard!', 'success');
      } catch (e) {
        this.showToast(shareUrl);
      }
    });

    // Native Web Share API button
    document.getElementById('lobby-share-btn')?.addEventListener('click', async () => {
      sound.playSfx('tap');
      if (navigator.share) {
        try {
          await navigator.share({
            title: `Play ${game.name} with me on FunShine!`,
            text: `Join my game room on FunShine! Room code: ${code}`,
            url: shareUrl
          });
        } catch (e) {}
      } else {
        await navigator.clipboard.writeText(shareUrl);
        this.showToast('Invite link copied!', 'success');
      }
    });

    // Presence update listener
    session.on('presence', ({ peers }) => {
      const guestBadge = document.getElementById('lobby-guest-badge');
      const startBtn = document.getElementById('lobby-start-btn');
      if (!guestBadge) return;

      const opponent = peers.find((p) => p.user_id !== auth.getUserId());
      if (opponent) {
        guestBadge.style.opacity = '1';
        guestBadge.innerHTML = `
          <span>👤 ${opponent.display_name || 'Opponent'}</span>
          <div class="peer-status-dot"></div>
        `;
        if (session.isHost && startBtn) {
          startBtn.disabled = false;
          startBtn.textContent = '⚔️ Start Match (Ready!)';
        }
      } else {
        guestBadge.style.opacity = '0.6';
        guestBadge.innerHTML = `<span>⏳ Waiting for opponent to join...</span>`;
      }
    });

    // Start match listener (for guest)
    session.on('matchStart', (match) => {
      this.closeModal();
      this.launchMultiplayerGame(session, game, match);
    });

    // Start button (for host)
    document.getElementById('lobby-start-btn')?.addEventListener('click', async () => {
      if (!session.isHost) return;
      sound.playSfx('success');
      const match = await multiplayer.startMatch(session);
      this.closeModal();
      this.launchMultiplayerGame(session, game, match);
    });
  }

  async launchMultiplayerGame(session, game, match) {
    this.destroyCurrentCartridge();
    this.currentCartridge = game;
    this.currentSession = session;

    const profile = auth.getProfile();
    const opponentPeer = Array.from(session.peers.values()).find((p) => p.user_id !== profile.id);
    const opponentName = opponentPeer ? opponentPeer.display_name : 'Opponent';

    this.mainView.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; width:100%; margin-bottom:8px;">
        <button id="game-exit-btn" class="btn btn-secondary btn-sm">← Leave Match</button>
        <span style="font-weight:700; font-size:0.9rem;">${game.icon} 1v1 vs ${opponentName}</span>
        <span style="font-size:0.75rem; background:var(--bg-surface); padding:4px 8px; border-radius:6px; font-family:var(--font-mono);">${session.room.code}</span>
      </div>
      <div id="cartridge-mount" class="cartridge-stage"></div>
    `;

    document.getElementById('game-exit-btn')?.addEventListener('click', () => {
      if (confirm('Leave this active match?')) {
        this.navigate('/');
      }
    });

    const mount = document.getElementById('cartridge-mount');

    await game.init(mount, {
      mode: 'multiplayer',
      playerId: profile.id,
      opponentId: opponentPeer ? opponentPeer.user_id : 'opponent',
      opponentName,
      isHost: session.isHost,
      config: session.room.config || {},
      audio: sound,
      haptics: {
        vibrate: (pattern) => sound.vibrate(pattern)
      },
      sendAction: (type, payload) => session.sendAction(type, payload),
      onGameOver: (result) => {
        auth.recordLocalSoloResult(result.winnerId === profile.id, result.isDraw);
        this.openGameOverModal(result, () => {
          this.openLobbyModal(session, game);
        });
      }
    });

    // Wire remote actions
    session.on('action', (action) => {
      game.handleRemoteAction(action);
    });

    session.on('opponentLeft', () => {
      this.showToast('Opponent disconnected. Waiting for reconnect...', 'error');
    });
  }

  /* -------------------------------------------------------------------------
     MODAL: Join Room via Code
     ------------------------------------------------------------------------- */
  openJoinCodeModal() {
    sound.playSfx('tap');
    this.openModal(`
      <div class="modal-header">
        <h3 class="modal-title">🔑 Enter Room Code</h3>
        <button class="btn-icon modal-close">✕</button>
      </div>
      <div>
        <div class="form-group">
          <label class="form-label">6-Character Room Code</label>
          <input id="input-join-code" class="form-input" placeholder="e.g. 7K4MZP" maxlength="6" style="text-transform:uppercase; font-family:var(--font-mono); font-size:1.4rem; text-align:center; letter-spacing:0.15em;" />
        </div>
        <button id="modal-submit-code-btn" class="btn btn-primary btn-block" style="margin-top:12px;">
          Join Room
        </button>
      </div>
    `);

    document.getElementById('modal-submit-code-btn')?.addEventListener('click', () => {
      const code = document.getElementById('input-join-code')?.value?.trim();
      if (code && code.length >= 4) {
        this.closeModal();
        this.navigate(`/join/${code.toUpperCase()}`);
      } else {
        this.showToast('Please enter a valid room code', 'error');
      }
    });
  }

  /* -------------------------------------------------------------------------
     MODAL: Game Over / Victory
     ------------------------------------------------------------------------- */
  openGameOverModal(result, onRematch) {
    const isWin = result.winnerId === auth.getUserId();
    const isDraw = result.isDraw;

    this.openModal(`
      <div style="text-align:center; padding:12px 0;">
        <div style="font-size:3.5rem; margin-bottom:8px;">
          ${isWin ? '👑🎉' : isDraw ? '🤝' : '💀'}
        </div>
        <h2 style="font-size:1.6rem; font-weight:800; color:${isWin ? 'var(--accent-amber)' : isDraw ? 'var(--accent-cyan)' : 'var(--accent-ruby)'};">
          ${isWin ? 'Victory!' : isDraw ? 'Draw Game!' : 'Defeat!'}
        </h2>
        <p style="color:var(--text-muted); font-size:0.95rem; margin:10px 0 20px;">
          ${result.reason || ''}
        </p>

        <div style="display:flex; flex-direction:column; gap:10px;">
          <button id="modal-rematch-btn" class="btn btn-primary btn-block">
            🔄 Play Rematch
          </button>
          <button id="modal-arcade-btn" class="btn btn-secondary btn-block">
            🕹️ Return to Arcade
          </button>
        </div>
      </div>
    `);

    document.getElementById('modal-rematch-btn')?.addEventListener('click', () => {
      this.closeModal();
      onRematch();
    });

    document.getElementById('modal-arcade-btn')?.addEventListener('click', () => {
      this.closeModal();
      this.navigate('/');
    });
  }

  /* -------------------------------------------------------------------------
     VIEW: Player Profile
     ------------------------------------------------------------------------- */
  renderProfileView() {
    const profile = auth.getProfile();
    const winRate = profile.games_played > 0 ? Math.round((profile.wins / profile.games_played) * 100) : 0;

    const AVATARS = ['🦊', '🐯', '🐼', '🐨', '🚀', '⭐', '⚡', '🔥', '🎲', '👑'];

    this.mainView.innerHTML = `
      <h1 style="font-size:1.4rem; font-weight:800; margin-bottom:14px;">Player Profile</h1>

      <div style="background:var(--bg-card); border:1px solid var(--border-subtle); border-radius:var(--radius-lg); padding:20px; box-shadow:var(--shadow-card); margin-bottom:16px;">
        <div style="display:flex; align-items:center; gap:16px; margin-bottom:16px;">
          <div style="font-size:2.8rem; width:72px; height:72px; border-radius:50%; background:var(--bg-surface); border:2px solid var(--accent-amber); display:flex; align-items:center; justify-content:center;">
            ${profile.avatar_emoji || '🎮'}
          </div>
          <div>
            <h2 style="font-size:1.3rem; font-weight:800;">${profile.display_name}</h2>
            <span style="font-size:0.8rem; color:var(--text-muted);">Anonymous Arcade Identity</span>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Display Name</label>
          <input id="profile-name-input" class="form-input" value="${profile.display_name}" maxlength="20" />
        </div>

        <div class="form-group">
          <label class="form-label">Avatar Emoji</label>
          <div style="display:flex; gap:8px; flex-wrap:wrap;">
            ${AVATARS.map((emoji) => `
              <button class="btn-avatar btn btn-secondary btn-sm" data-emoji="${emoji}" style="font-size:1.3rem; padding:6px 12px; ${profile.avatar_emoji === emoji ? 'border-color:var(--accent-amber); background:var(--bg-card-hover);' : ''}">
                ${emoji}
              </button>
            `).join('')}
          </div>
        </div>

        <button id="profile-save-btn" class="btn btn-primary btn-block" style="margin-top:14px;">
          Save Profile
        </button>
      </div>

      <!-- Stats Grid -->
      <h3 style="font-size:1rem; font-weight:700; margin-bottom:10px;">Personal Record</h3>
      <div style="display:grid; grid-template-columns:repeat(2, 1fr); gap:12px;">
        <div style="background:var(--bg-surface); border:1px solid var(--border-subtle); border-radius:var(--radius-md); padding:14px; text-align:center;">
          <div style="font-size:1.8rem; font-weight:800; color:var(--accent-amber);">${profile.games_played || 0}</div>
          <div style="font-size:0.8rem; color:var(--text-muted);">Games Played</div>
        </div>
        <div style="background:var(--bg-surface); border:1px solid var(--border-subtle); border-radius:var(--radius-md); padding:14px; text-align:center;">
          <div style="font-size:1.8rem; font-weight:800; color:var(--accent-emerald);">${profile.wins || 0}</div>
          <div style="font-size:0.8rem; color:var(--text-muted);">Victories</div>
        </div>
        <div style="background:var(--bg-surface); border:1px solid var(--border-subtle); border-radius:var(--radius-md); padding:14px; text-align:center;">
          <div style="font-size:1.8rem; font-weight:800; color:var(--accent-cyan);">${winRate}%</div>
          <div style="font-size:0.8rem; color:var(--text-muted);">Win Rate</div>
        </div>
        <div style="background:var(--bg-surface); border:1px solid var(--border-subtle); border-radius:var(--radius-md); padding:14px; text-align:center;">
          <div style="font-size:1.8rem; font-weight:800; color:var(--accent-violet);">${profile.best_streak || 0}🔥</div>
          <div style="font-size:0.8rem; color:var(--text-muted);">Best Streak</div>
        </div>
      </div>
    `;

    let selectedEmoji = profile.avatar_emoji || '🎮';

    this.mainView.querySelectorAll('.btn-avatar').forEach((btn) => {
      btn.addEventListener('click', () => {
        selectedEmoji = btn.getAttribute('data-emoji');
        this.mainView.querySelectorAll('.btn-avatar').forEach((b) => (b.style.borderColor = 'var(--border-subtle)'));
        btn.style.borderColor = 'var(--accent-amber)';
        sound.playSfx('tap');
      });
    });

    document.getElementById('profile-save-btn')?.addEventListener('click', async () => {
      const name = document.getElementById('profile-name-input')?.value?.trim();
      if (!name) return;
      await auth.updateProfile({ display_name: name, avatar_emoji: selectedEmoji });
      sound.playSfx('success');
      this.showToast('Profile updated!', 'success');
    });
  }

  /* -------------------------------------------------------------------------
     VIEW: Leaderboards
     ------------------------------------------------------------------------- */
  renderLeaderboardsView() {
    const profile = auth.getProfile();

    this.mainView.innerHTML = `
      <h1 style="font-size:1.4rem; font-weight:800; margin-bottom:4px;">Rankings & Best Scores</h1>
      <p style="font-size:0.85rem; color:var(--text-muted); margin-bottom:14px;">
        Track personal high scores and global standings across all 4 arcade games.
      </p>

      <div style="display:flex; flex-direction:column; gap:12px;">
        <div style="background:var(--bg-card); border:1px solid var(--border-subtle); border-radius:var(--radius-md); padding:14px; display:flex; justify-content:space-between; align-items:center;">
          <div style="display:flex; align-items:center; gap:12px;">
            <span style="font-size:1.6rem;">🥇</span>
            <div>
              <div style="font-weight:700;">Gomoku AI Duel</div>
              <div style="font-size:0.8rem; color:var(--text-muted);">Master Difficulty AI Cleared</div>
            </div>
          </div>
          <span style="font-weight:800; color:var(--accent-amber);">Rank #1</span>
        </div>

        <div style="background:var(--bg-card); border:1px solid var(--border-subtle); border-radius:var(--radius-md); padding:14px; display:flex; justify-content:space-between; align-items:center;">
          <div style="display:flex; align-items:center; gap:12px;">
            <span style="font-size:1.6rem;">🥈</span>
            <div>
              <div style="font-weight:700;">Battleship Fleet Command</div>
              <div style="font-size:0.8rem; color:var(--text-muted);">Least Shots to Sink Fleet: 28</div>
            </div>
          </div>
          <span style="font-weight:800; color:var(--accent-amber);">${profile.wins > 0 ? profile.wins + ' Wins' : '1 Fleet'}</span>
        </div>

        <div style="background:var(--bg-card); border:1px solid var(--border-subtle); border-radius:var(--radius-md); padding:14px; display:flex; justify-content:space-between; align-items:center;">
          <div style="display:flex; align-items:center; gap:12px;">
            <span style="font-size:1.6rem;">🥉</span>
            <div>
              <div style="font-weight:700;">Bulls & Cows Daily Challenge</div>
              <div style="font-size:0.8rem; color:var(--text-muted);">Cracked in 6 Attempts</div>
            </div>
          </div>
          <span style="font-weight:800; color:var(--accent-cyan);">6 Tries</span>
        </div>

        <div style="background:var(--bg-card); border:1px solid var(--border-subtle); border-radius:var(--radius-md); padding:14px; display:flex; justify-content:space-between; align-items:center;">
          <div style="display:flex; align-items:center; gap:12px;">
            <span style="font-size:1.6rem;">⭐</span>
            <div>
              <div style="font-weight:700;">Hangman Duel</div>
              <div style="font-size:0.8rem; color:var(--text-muted);">Best Word Streak</div>
            </div>
          </div>
          <span style="font-weight:800; color:var(--accent-emerald);">${profile.best_streak || 0} Streak</span>
        </div>
      </div>
    `;
  }

  /* -------------------------------------------------------------------------
     VIEW: Settings
     ------------------------------------------------------------------------- */
  renderSettingsView() {
    const isConfigured = isSupabaseConfigured();

    this.mainView.innerHTML = `
      <h1 style="font-size:1.4rem; font-weight:800; margin-bottom:14px;">Settings & Preferences</h1>

      <div style="background:var(--bg-card); border:1px solid var(--border-subtle); border-radius:var(--radius-lg); padding:20px; margin-bottom:16px;">
        <h3 style="font-size:1rem; font-weight:700; margin-bottom:12px;">Audio & Haptics</h3>

        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px;">
          <div>
            <div style="font-weight:600;">Sound Effects (SFX)</div>
            <div style="font-size:0.8rem; color:var(--text-muted);">Procedural retro arcade audio</div>
          </div>
          <input type="checkbox" id="setting-sfx-toggle" ${sound.enabled ? 'checked' : ''} style="width:22px; height:22px; accent-color:var(--accent-amber); cursor:pointer;" />
        </div>

        <div class="form-group" style="margin-bottom:16px;">
          <label class="form-label">Volume: <span id="vol-display">${Math.round(sound.volume * 100)}%</span></label>
          <input type="range" id="setting-sfx-volume" min="0" max="1" step="0.05" value="${sound.volume}" style="width:100%; accent-color:var(--accent-amber);" />
        </div>

        <div style="display:flex; justify-content:space-between; align-items:center;">
          <div>
            <div style="font-weight:600;">Haptic Feedback</div>
            <div style="font-size:0.8rem; color:var(--text-muted);">Vibrate on moves and impacts</div>
          </div>
          <input type="checkbox" id="setting-haptic-toggle" ${sound.hapticsEnabled ? 'checked' : ''} style="width:22px; height:22px; accent-color:var(--accent-amber); cursor:pointer;" />
        </div>
      </div>

      <!-- Supabase Backend Configuration -->
      <div style="background:var(--bg-card); border:1px solid var(--border-subtle); border-radius:var(--radius-lg); padding:20px;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
          <h3 style="font-size:1rem; font-weight:700;">Backend & Cloud Connection</h3>
          <span style="font-size:0.75rem; font-weight:700; padding:3px 8px; border-radius:6px; background:${isConfigured ? '#065f46' : '#7f1d1d'}; color:#fff;">
            ${isConfigured ? '🟢 Supabase Connected' : '🟡 Local Mode'}
          </span>
        </div>

        <p style="font-size:0.85rem; color:var(--text-muted); margin-bottom:14px;">
          ${isConfigured
            ? `Connected to: <code>${SUPABASE_URL}</code>`
            : 'Multiplayer is currently running via local tab broadcast. Enter your Supabase credentials to activate real-time internet multiplayer across devices.'}
        </p>

        <div class="form-group">
          <label class="form-label">Supabase Project URL</label>
          <input id="setting-supabase-url" class="form-input" placeholder="https://xxxx.supabase.co" value="${SUPABASE_URL || ''}" />
        </div>

        <div class="form-group">
          <label class="form-label">Supabase Anon Key</label>
          <input id="setting-supabase-key" class="form-input" placeholder="eyJhbGciOi..." type="password" />
        </div>

        <div style="display:flex; gap:10px; margin-top:14px;">
          <button id="setting-save-db-btn" class="btn btn-primary" style="flex:1;">
            💾 Connect Backend
          </button>
          ${isConfigured ? '<button id="setting-reset-db-btn" class="btn btn-secondary" style="flex:1;">Reset</button>' : ''}
        </div>
      </div>
    `;

    document.getElementById('setting-sfx-toggle')?.addEventListener('change', (e) => {
      sound.setEnabled(e.target.checked);
      if (e.target.checked) sound.playSfx('tap');
    });

    document.getElementById('setting-sfx-volume')?.addEventListener('input', (e) => {
      const vol = parseFloat(e.target.value);
      sound.setVolume(vol);
      const display = document.getElementById('vol-display');
      if (display) display.textContent = `${Math.round(vol * 100)}%`;
    });

    document.getElementById('setting-haptic-toggle')?.addEventListener('change', (e) => {
      sound.setHapticsEnabled(e.target.checked);
      if (e.target.checked) sound.vibrate([30]);
    });

    document.getElementById('setting-save-db-btn')?.addEventListener('click', () => {
      const url = document.getElementById('setting-supabase-url')?.value?.trim();
      const key = document.getElementById('setting-supabase-key')?.value?.trim();
      if (url && key) {
        setCustomCredentials(url, key);
      } else {
        this.showToast('Please provide both Project URL and Anon Key', 'error');
      }
    });

    document.getElementById('setting-reset-db-btn')?.addEventListener('click', () => {
      clearCustomCredentials();
    });
  }

  /* -------------------------------------------------------------------------
     MODAL CONTROLLER
     ------------------------------------------------------------------------- */
  openModal(innerHtml) {
    this.modalContainer.innerHTML = `
      <div class="modal-overlay open" id="modal-overlay">
        <div class="modal-content">
          ${innerHtml}
        </div>
      </div>
    `;

    this.modalContainer.querySelectorAll('.modal-close').forEach((btn) => {
      btn.addEventListener('click', () => this.closeModal());
    });

    document.getElementById('modal-overlay')?.addEventListener('click', (e) => {
      if (e.target.id === 'modal-overlay') {
        this.closeModal();
      }
    });
  }

  closeModal() {
    const overlay = document.getElementById('modal-overlay');
    if (overlay) {
      overlay.classList.remove('open');
      setTimeout(() => (this.modalContainer.innerHTML = ''), 200);
    }
  }
}

// Bootstrap application on DOM load
window.addEventListener('DOMContentLoaded', () => {
  const app = new AppShell();
  app.init();
});
